/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useEffect } from 'react';

/**
 * Returns the highest computed z-index for the siblings of a given element
 */
export const getMaxZIndex = element => {
    let currentElem = element.parentNode.firstChild;
    const siblings = [];
    for (; currentElem; currentElem = currentElem.nextSibling) {
        if (currentElem.nodeType === 1 && currentElem !== element) siblings.push(currentElem);
    }

    return Math.max(
        ...Array.from(siblings, el => parseFloat(window.getComputedStyle(el).zIndex)).filter(
            zIndex => !Number.isNaN(zIndex)
        ),
        0
    );
};

const getMaxZIndexOfChildren = element => {
    let maxZIndex = 0;
    let children = element.children;
    for (let i = 0; i < children.length; i++) {
        let zIndex = window.getComputedStyle(children.item(i)).zIndex;
        zIndex = Number.isNaN(zIndex) ? 0 : zIndex;
        maxZIndex = Math.max(maxZIndex, zIndex);
    }
    return maxZIndex;
};

/**
 * Check if the given reference DOM element is used in a RS dialog. If yes,
 * update style on RS root element to be above typical CoralUI dialogs
 * (e.g. in AEM Sites editor).
 */
export const useOverlayFix = ref => {
    useEffect(() => {
        if (!ref || !ref.current) {
            return;
        }

        let inDialog = false;
        let spectrumRoot = null;

        for (let currentElem = ref.current; currentElem; currentElem = currentElem.parentElement) {
            const classes = Array.from(currentElem.classList);

            if (classes.find(c => c.startsWith('_spectrum-Dialog_'))) {
                inDialog = true;
            }
            if (classes.find(c => c.startsWith('_spectrum_'))) {
                spectrumRoot = currentElem;
            }

            if (currentElem.nodeName === 'BODY') {
                break;
            }
        }

        // check content fragment editor
        let inContentFragmentEditor = document.querySelector('#aem-cfm-editor-elements-form');
        if (inContentFragmentEditor && spectrumRoot) {
            let contentFragmentEditorPanel = document.querySelector('#Content.editor-panel');
            if (contentFragmentEditorPanel) {
                let maxZIndex = getMaxZIndexOfChildren(contentFragmentEditorPanel);
                spectrumRoot.style.position = 'absolute';
                spectrumRoot.style.zIndex = maxZIndex + 10;
                return;
            }
        }

        if (inDialog && spectrumRoot) {
            const maxZIndex = getMaxZIndex(spectrumRoot);
            spectrumRoot.style.position = 'absolute';
            spectrumRoot.style.zIndex = maxZIndex + 10;
        }
    }, [ref && ref.current]);
};
